// SAB-575: social auth buttons, shared by the /signup + /login screens and the
// desktop inline signup card. Social-first per the handoff. Apple is MANDATORY
// on iOS (App Store guideline 4.8 — offering Google requires offering Apple).
//
// NOTE: Google/Apple OAuth providers are NOT yet enabled in Supabase. These
// buttons are wired to supabase.auth.signInWithOAuth via the parent's onProvider
// handler; until the providers are configured in the Supabase dashboard they
// will error. Provider setup is a separate, flagged step (see SAB-575 report).
import { Capacitor } from '@capacitor/core';
import { cn } from '@/lib/utils';
import { useNativeAppVersion } from '@/hooks/useNativeAppVersion';
import { NATIVE_OAUTH_MIN_VERSION } from '@/lib/nativeOAuthGate';

export type OAuthProvider = 'google' | 'apple';

// Official-style brand marks (inline SVG). Follow each provider's button
// guidelines: Google on white with the 4-color G; Apple with the white glyph.
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.71H.96v2.33A9 9 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.98 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l3.02-2.33z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.02 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
    </svg>
  );
}

function AppleMark() {
  return (
    <svg width="17" height="20" viewBox="0 0 17 20" fill="currentColor" aria-hidden focusable="false">
      <path d="M14.09 10.62c-.02-2.02 1.65-2.99 1.72-3.04-.94-1.37-2.4-1.56-2.92-1.58-1.24-.13-2.42.73-3.05.73-.63 0-1.6-.71-2.63-.69-1.35.02-2.6.79-3.29 2-1.4 2.43-.36 6.03 1 8 .67.96 1.46 2.04 2.5 2 1-.04 1.38-.65 2.6-.65 1.2 0 1.55.65 2.6.63 1.08-.02 1.76-.98 2.42-1.95.76-1.12 1.07-2.2 1.09-2.26-.02-.01-2.09-.8-2.11-3.17zM12.07 4.63c.55-.67.92-1.6.82-2.53-.79.03-1.76.53-2.33 1.2-.51.59-.96 1.53-.84 2.43.88.07 1.79-.45 2.35-1.1z"/>
    </svg>
  );
}

interface Props {
  onProvider: (provider: OAuthProvider) => void;
  disabled?: boolean;
  /** 'screen' = 52px/radius12 (auth pages); 'card' = 50px/radius11 (desktop card). */
  variant?: 'screen' | 'card';
}

export function SocialAuthButtons({ onProvider, disabled, variant = 'screen' }: Props) {
  const { atLeast } = useNativeAppVersion();
  // SAB (native OAuth gate, now version-aware for Build 6 Phase A): OAuth needs the
  // appUrlOpen deep-link return, which only exists on native 1.2.0+. Hide Google/Apple
  // ONLY on native builds below 1.2.0; web (isNativePlatform() false) and native 1.2.0+
  // render both. Fail-safe: gated while App.getInfo() is unresolved/unavailable. The
  // hook runs before this early return, so hook order is stable.
  if (Capacitor.isNativePlatform() && !atLeast(NATIVE_OAUTH_MIN_VERSION)) return null;

  const h = variant === 'card' ? 'h-[50px]' : 'h-[52px]';
  const radius = variant === 'card' ? 'rounded-[11px]' : 'rounded-xl';
  const base = cn(
    'w-full flex items-center justify-center gap-2.5 font-bold text-[15.5px] transition-colors disabled:opacity-60',
    h, radius,
  );

  return (
    <div className="flex flex-col gap-2.5">
      <button
        type="button"
        onClick={() => onProvider('google')}
        disabled={disabled}
        className={cn(base, 'bg-white text-[#111] hover:bg-white/90')}
      >
        <GoogleMark />
        Continue with Google
      </button>
      <button
        type="button"
        onClick={() => onProvider('apple')}
        disabled={disabled}
        className={cn(base, 'bg-white/[0.04] text-foreground border border-white/[0.14] hover:bg-white/[0.07]')}
      >
        <AppleMark />
        Continue with Apple
      </button>
    </div>
  );
}

export default SocialAuthButtons;
